/** @jsxRuntime classic */
/** @jsx jsx */
import { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { css, jsx } from '@emotion/react';
import { Link } from 'react-router-dom';

import { Typography as Text } from '@mui/material';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import { useTranscriptUploadActions } from '../../actions/FileActions';

import { useUserQuery } from '../../actions/UserActions';
import { useCreditsQuery } from '../../actions/CreditsActions';
import { track } from '../../utils/track';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import InputAdornment from '@mui/material/InputAdornment';
import WarningIcon from '@mui/icons-material/WarningOutlined';
import { validatePhoneNumber } from '../../utils/phone';
import CircularProgress from '@mui/material/CircularProgress';
import VerficationInput from '../common/VerificationInput';

import DialogHeader from './DialogHeader';

import { throttle } from '../../utils/functions';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';
import Collapse from '@mui/material/Collapse';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ThumbsUpIcon from '@mui/icons-material/ThumbUp';

import UploadSettings from './UploadSettings';
import { useUserActions } from '../../actions/UserActions';
import Alert from '@mui/material/Alert';

function PhoneWithWarning() {
  return (
    <div
      css={css`
        display: flex;
      `}
    >
      <div
        css={css`
          background-color: rgba(0, 0, 0, 0.12);
          border-radius: 50%;
          width: 40px;
          height: 40px;

          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        <PhoneOutlinedIcon
          fontSize="medium"
          css={css`
            color: rgba(0, 0, 0, 0.54);
            padding: 0;
          `}
        />
      </div>
      <WarningIcon
        fontSize="small"
        color="warning"
        css={css`
          position: relative;
          left: -14px;
          top: -5px;
        `}
      />
    </div>
  );
}

export default function UploadPhone({ handleClose }) {
  const { t } = useTranslation();
  const { uploadTranscriptAsync } = useTranscriptUploadActions();
  const {
    updateUserAsync,
    sendVerificationPhone,
    sendVerificationPhoneAsync,
    verifyPhone,
    isUpdateUserLoading,
    isSendVerificationPhoneLoading,
    isVerifyPhoneError,
  } = useUserActions();

  const throttledVerifyPhone = useMemo(
    () => throttle(sendVerificationPhone, 1000),
    [sendVerificationPhone]
  );

  const { user } = useUserQuery();
  const { credits } = useCreditsQuery();
  const [settings, setSettings] = useState({
    numSpeakers: 0,
    language: 'auto',
    outputFormat: 'transcript',
    transcribeMode: 0,
    name: new Date().toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
  });

  const allowPhone = user?.workspace?.allow_phone;
  const isEnterprise = user?.workspace?.type === 'enterprise';

  const [phone, setPhone] = useState('');

  const [validationStep, setValidationStep] = useState(0);
  const [validationPhone, setValidationPhone] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [called, setCalled] = useState(false);

  const validationPhoneNumberError = useMemo(() => {
    const error = validatePhoneNumber(validationPhone, t);
    if (error?.error) {
      return error.desc;
    }
    return null;
  }, [validationPhone, t]);

  const phoneError = useMemo(() => {
    const error = validatePhoneNumber(phone, t);
    if (error?.error) {
      return error.desc;
    }
    return null;
  }, [phone, t]);

  async function onUpload() {
    if (!user.phone || !phone || phone === user.phone) return;
    await uploadTranscriptAsync({
      ...settings,
      url: 'phone',
      from: user.phone,
      to: phone,
    });
    setCalled(true);
  }

  async function updatePhone() {
    await updateUserAsync({ phone: validationPhone });
    await sendVerificationPhoneAsync();
    setValidationStep(1);
  }

  if (!allowPhone) {
    return (
      <div>
        <DialogHeader
          title={t('Spela in telefonsamtal')}
          icon={PhoneOutlinedIcon}
          onClose={handleClose}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          `}
        >
          <PhoneWithWarning />
          <Text variant="subtitle1">
            {t('Ditt konto har inte tillgång till denna funktionen.')}
          </Text>
          <Text variant="subtitle2" color="text.secondary">
            {isEnterprise
              ? t('Kontakta oss för att uppgradera ditt konto.')
              : t(
                  'Uppgradera ditt konto för att få tillgång till denna funktionen.'
                )}
          </Text>
          <Button
            onClick={() => {
              track('credits_phone_buy_click');
              handleClose();
            }}
            to={'/support'}
            css={css`
              margin-left: 0.125rem;
            `}
            variant="contained"
            fullWidth
          >
            {isEnterprise ? t('Kontakta oss') : t('Uppgradera')}
          </Button>
        </div>
      </div>
    );
  }

  if (!credits?.phone?.credits) {
    return (
      <div>
        <DialogHeader
          title={t('Spela in telefonsamtal')}
          icon={PhoneOutlinedIcon}
          onClose={handleClose}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          `}
        >
          <PhoneWithWarning />
          <Text variant="subtitle1">
            {t('Du har slut på telefonsamtal för månaden.')}
          </Text>
          <Text variant="subtitle2" color="text.secondary">
            {isEnterprise
              ? t(
                  'Kontakta oss för att uppgradera ditt konto till fler telefonsamtal.'
                )
              : t(
                  'Uppgradera ditt konto för att få tillgång till denna funktionen.'
                )}
          </Text>
          <Button
            variant="contained"
            to={isEnterprise ? '/support' : '/profile/billing'}
            LinkComponent={Link}
            onClick={() => {
              track('credits_phone_buy_click');
              handleClose();
            }}
            fullWidth
          >
            {t('Uppgradera för fler telefonsamtal')}
          </Button>
        </div>
      </div>
    );
  }

  if (!user?.phone_verified_at) {
    return (
      <div>
        <DialogHeader
          title={t('Spela in telefonsamtal')}
          icon={PhoneOutlinedIcon}
          onClose={handleClose}
        />
        <div
          css={css`
            display: flex;
            justify-content: center;
          `}
        >
          {validationStep === 0 && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 1rem;
              `}
            >
              <Text
                variant="subtitle2"
                color="text.secondary"
                fontWeight="600"
                textAlign="center"
              >
                {t(
                  'Du behöver verifiera ditt telefonnummer innan du kan börja ringa samtal'
                )}
              </Text>
              <div>
                <Text variant="subtitle2" fontWeight="600">
                  {t('Ditt telefonnummer')}
                </Text>
                <TextField
                  value={validationPhone}
                  onChange={(e) => setValidationPhone(e.target.value)}
                  error={validationPhoneNumberError}
                  helperText={validationPhoneNumberError}
                  size="small"
                  placeholder="+46"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon
                          sx={{
                            color: user?.phone_verified_at
                              ? 'success.main'
                              : 'warning.main',
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => updatePhone()}
                disabled={validationPhoneNumberError}
                css={css`
                  margin-left: auto;
                `}
                endIcon={
                  (isUpdateUserLoading || isSendVerificationPhoneLoading) && (
                    <CircularProgress size={20} />
                  )
                }
              >
                {t('Skicka verifieringskod')}
              </Button>
            </div>
          )}
          {validationStep === 1 && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 1rem;
                align-items: center;
                max-width: 360px;
              `}
            >
              <Text
                variant="subtitle2"
                color="text.secondary"
                fontWeight="600"
                textAlign="center"
              >
                <Trans t={t}>
                  Vi har skickat en sexsiffrig kod till ditt telefonnummer{' '}
                  <strong>{{ phone: validationPhone }}</strong>
                </Trans>
              </Text>
              <VerficationInput onChange={setValidationCode} middleBreak />
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                `}
              >
                <InfoOutlinedIcon fontSize="small" color="disabled" />
                <Text
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight="600"
                  onClick={() => {
                    track('credits_phone_resend_verification_click');
                    throttledVerifyPhone();
                  }}
                >
                  <Trans t={t}>
                    Fick du inget SMS-meddelande?{' '}
                    <mark
                      css={css`
                        cursor: pointer;
                        text-decoration-color: var(--primary);
                        text-decoration-line: underline;
                        background-color: white;
                        font-weight: 700;
                      `}
                    >
                      Skicka koden igen
                    </mark>
                  </Trans>
                </Text>
              </div>
              <Collapse in={isVerifyPhoneError}>
                <div>
                  <Text
                    variant="subtitle2"
                    color="error"
                    fontWeight="600"
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 0.5rem;
                    `}
                  >
                    <WarningIcon fontSize="small" />
                    {t(
                      'Vi kunde inte verifiera ditt telefonnummer. Vänligen kontrollera att du angav rätt kod.'
                    )}
                  </Text>
                </div>
              </Collapse>
              <Button
                variant="contained"
                color="primary"
                onClick={() => verifyPhone(validationCode)}
                disabled={!validationCode}
                css={css`
                  margin-left: auto;
                `}
                endIcon={isUpdateUserLoading && <CircularProgress size={20} />}
              >
                {t('Verifiera')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (called) {
    return (
      <div>
        <DialogHeader
          title={t('Spela in telefonsamtal')}
          icon={PhoneOutlinedIcon}
          onClose={handleClose}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          `}
        >
          <div
            css={css`
              background-color: var(--success-4p);
              border-radius: 50%;
              width: 54px;
              height: 54px;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <ThumbsUpIcon color="info" fontSize="large" />
          </div>
          <Text>{t('Vi ringer upp dig alldeles strax.')}</Text>
          <Text
            variant="subtitle2"
            color="text.secondary"
            textAlign="center"
            maxWidth="360px"
          >
            {t(
              'När du svarar kommer du att få vidare information om hur du kopplas vidare'
            )}
          </Text>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleClose()}
            fullWidth
          >
            {t('Stäng fönster')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DialogHeader
        title={t('Spela in telefonsamtal')}
        icon={PhoneOutlinedIcon}
        onClose={handleClose}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 1rem;
        `}
      >
        <Alert severity="info" variant="outlined" icon={<InfoIcon />}>
          <Text variant="subtitle2" color="text.secondary">
            <Trans t={t}>
              Klang.ai kommer att ringa upp dig på ditt telefonnummer, därefter
              kopplas du vidare till telefonnumret du anger nedan.
            </Trans>
          </Text>
        </Alert>
        <div>
          <Text variant="subtitle2" fontWeight="600">
            {t('Telefonnummer att ringa upp')}
          </Text>
          <TextField
            variant="outlined"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneError}
            helperText={phoneError}
            placeholder="+46"
            noAutocomplete
            autoComplete="off"
          />
        </div>
        <Button
          variant="contained"
          color="error"
          endIcon={<FiberManualRecordIcon />}
          fullWidth
          css={css`
            border-radius: 2rem;
          `}
          disabled={
            !settings.name ||
            !settings.outputFormat ||
            !phone?.length ||
            phoneError ||
            settings?.folderPrefix?.split('/')?.length <= 2
          }
          onClick={onUpload}
        >
          {t('Ring upp & spela in')}
        </Button>
        <UploadSettings
          settings={settings}
          setSettings={setSettings}
          nameEdit
        />
      </div>
    </div>
  );
}
